import type { Point2d } from ".."
import type { DivideInsetFaceProcedure } from "./divideInsetFace"
import { divideInsetFace } from "./divideInsetFace"
import type { FilterInsetAreasProcedure } from "./filterInsetAreas"
import { filterInsetAreas } from "./filterInsetAreas"
import { findAllIntersections } from "./findAllIntersections"
import type { InsetEdge } from "./getInsetEdges"
import { getInsetEdges } from "./getInsetEdges"
import { getTangledInsetFace } from "./getTangledInsetFace"
import { retrieveFromCache, storeInCache } from "./insetAreaCache"
import type { SerialTag } from "./linkAllInsetEdges"
import { linkAllInsetEdges } from "./linkAllInsetEdges"
import type { InsetLinkEdge } from "./linkInsetEdges"
import type { NameVirtualNode } from "./nameVirtualNode"
import { nameVirtualNode } from "./nameVirtualNode"
import type { SimplifyInsetEdgesProcedure } from "./simplifyInsetEdges"
import { simplifyInsetEdges } from "./simplifyInsetEdges"
import type { Face2dComputed, Edge2d, Edge2dComputed } from "../../mesh"

export type GetInsetAreasOptions = {
  areaId: string
  area: Face2dComputed
  edges: (Edge2d & Edge2dComputed & { setback: number })[]
  makeNodeId?: NameVirtualNode
}

/* prettier-ignore */
export type GetInsetAreaProcedure = 
  DivideInsetFaceProcedure &
  FilterInsetAreasProcedure &
  SimplifyInsetEdgesProcedure & {
    dividedAreas: Face2dComputed[]
    linksBetweenInsets: Edge2dComputed[][]
    insetsAndLinks: ((InsetEdge & SerialTag) | (InsetLinkEdge & SerialTag))[]
    intersections: (Point2d & SerialTag)[]
  }
/* prettier-ignore-end */

export const getInsetAreas = ({
  areaId,
  area,
  edges,
  makeNodeId = nameVirtualNode,
}: GetInsetAreasOptions): {
  result: Face2dComputed[]
  procedure: GetInsetAreaProcedure
} => {
  const cachedValue = retrieveFromCache(areaId, edges)
  if (`hit` in cachedValue) return cachedValue.hit

  const key = cachedValue.miss

  const insetEdges = getInsetEdges({ area, edges })

  const {
    result: { insetsAndLinks },
    procedure: { linksBetweenInsets },
  } = linkAllInsetEdges({ insetEdges })

  const {
    procedure: { intersections },
  } = findAllIntersections({ insetsAndLinks })

  const {
    result: { insetEdgesSimplified },
    procedure: { nodesEliminated },
  } = simplifyInsetEdges({ insetsAndLinks })

  const {
    result: tangledInsetFace,
    procedure: { placeholder: getTangledInsetFaceProcedure },
  } = getTangledInsetFace({ insetEdgesSimplified })

  const {
    result: dividedAreas,
    procedure: { dividedMeshNodeGroups, meshIntersections },
  } = divideInsetFace({ tangledInsetFace, makeNodeId })

  const {
    result: filteredAreas,
    procedure: { nodeEdgePairsThatAreTooClose, nodesNotInOriginalArea },
  } = filterInsetAreas({ dividedAreas, area, edges: insetEdges })

  const returnValue = {
    result: filteredAreas,
    procedure: {
      dividedMeshNodeGroups,
      dividedAreas,
      nodeEdgePairsThatAreTooClose,
      nodesNotInOriginalArea,
      meshIntersections,
      nodesEliminated,
      linksBetweenInsets,
      insetsAndLinks,
      intersections,
    },
  }

  storeInCache(areaId, key, returnValue)

  return returnValue
}
